export type LocalDocumentFileFormat =
    | 'TXT'
    | 'PDF'
    | 'JPEG'
    | 'JPG'
    | 'PNG'
    | 'DOCX'
    | 'DOC'
    | 'XLSX'
    | 'PPTX'
    | 'ODT';

export type SupportedMimeType =
    | 'application/pdf'
    | 'image/png'
    | 'image/jpeg'
    | 'text/plain';

export type LocalDocumentContentStatus =
    | 'TODO'
    | 'PROCESSING'
    | 'DONE'
    | 'ERROR'
    | 'ARCHIVED'
    | 'APPROVED';

export type LocalDocumentCategory  =
    | 'MEDICAL_HISTORY'
    | 'TEST_RESULTS'
    | 'REFERRAL'
    | 'STATEMENT'
    | 'OTHER'
    | 'NOTYPE'
    | 'UNRECOGNIZED';
//     | 'PRESCRIPTION'
//     | 'DISCHARGE'

export type LocalDocumentSourceLanguage = 'PL' | string;

export type LocalDocumentSortColumn =
    | 'FILE_NAME'
    | 'PESEL'
    | 'CREATED_AT'
    | 'TYPE';

export type LocalDocumentContentOperationType =
    | 'PENDING_APPROVAL'
    | 'APPROVED';

export type LocalDocumentExportFormat = 'XLSX';

export const LOCAL_DOCUMENTS_UNASSIGNABLE_TYPE = 'UNRECOGNIZED';

// TODO: verify if Partial can be removed
export const SORT_COLUMN_MAP: Partial<Record<keyof LocalDocumentContentResponse, LocalDocumentSortColumn>> = {
    fileName: 'FILE_NAME',
    peselNumber: 'PESEL',
    createdAt: 'CREATED_AT',
    type: 'TYPE',
};

export const LOCAL_DOCUMENTS_INIT_SORT_ORDER = 'asc';

export const LOCAL_DOCUMENTS_PAGE_SIZE_OPTIONS = [20, 50, 100];

export const LOCAL_DOCUMENTS_INIT_PAGE_SIZE = 50;

export interface LocalDocumentSortType {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
}

export interface LocalDocumentEntity {
    patientId: number;
    patientPesel: string;
    patientNumber: string;
    patientFirstName: string;
    patientLastName: string;
    visitId: number;
    visitNumber: string;
}

export interface LocalDocumentContentResponse extends LocalDocumentEntity {
    id: number;
    documentId: number;
    localDocumentId: number;
    contentType: LocalDocumentFileFormat;
    fileFormat: string;
    numbOfPages: number;
    size: number;
    fileName: string;
    peselNumber: number;
    createdAt: string;
    status: LocalDocumentContentStatus;
    type: string | LocalDocumentCategory;
    typeName: string;
    sentBy: string;
    verifiedBy: string;
    verifiedAt: string;
    approvalStatus: LocalDocumentContentOperationType;
    approvalStatusName: string;
    sourceLanguage: LocalDocumentSourceLanguage;
    packageId: string;
    confidencePercent: number;
}

export interface LocalDocumentsPageableResponse  {
    offset: number;
    sort: LocalDocumentSortType;
    pageSize: number;
    paged: boolean;
    pageNumber: number;
    unpaged: boolean;
}

// search and get response
export interface LocalDocumentsResponse {
    totalElements: number;
    totalPages: number;
    empty: boolean;
    first: boolean;
    last: boolean;
    size: number;
    content: LocalDocumentContentResponse[];
    number: number;
    sort: LocalDocumentSortType;
    numberOfElements: number;
    pageable: LocalDocumentsPageableResponse;
}

// GET /processing/documents params
export interface LocalDocumentsQueryParams {
    pageNumber?: number;
    pageSize?: number;
    sortColumn?: LocalDocumentSortColumn;
    sortOrder?: string;
}

// GET /processing/documents/search params
export interface LocalDocumentsSearchQueryParams {
    fileName?: string;
    pesel?: number;
    dateFrom?: string;
    dateTo?: string;
    status?: LocalDocumentContentStatus;
    type?: string;
    packageId?: string;
    sortColumn?: LocalDocumentSortColumn;
    sortOrder?: string;
    pageNumber?: number;
    pageSize?: number;
}

export interface LocalDocumentHistoryResponse {
    id: number;
    operation: LocalDocumentContentOperationType;
    operationName: string;
    performedBy: string;
    performedAt: string;
}

export interface LocalDocumentPreviewResponse {
    id: number;
    fileName: string;
    contentType: LocalDocumentFileFormat;
    fileFormat: string;
    mediaType: string;
    size: number;
    pageCount: number;
}

export interface OcrVersionPagesWithText {
    pages: {
        pageNumber: number;
        text: string;
        confidencePercent: number;
    }[];
}

export interface LocalDocumentOcrVersionResponse extends OcrVersionPagesWithText {
    id: number;
    pageCount: number;
}

// GET /processing/documents/navigation params
export interface LocalDocumentNavigationQueryParams {
    fileName?: string;
    pesel?: number;
    dateFrom?: string;
    dateTo?: string;
    status?: LocalDocumentContentStatus;
    type?: string;
    packageId?: string;
    sortColumn?: LocalDocumentSortColumn;
    sortOrder?: string;
}

export interface LocalDocumentNavigationData {
    previousDocumentId: number;
    nextDocumentId: number;
    position: number;
    totalElements: number;
    hasPrevious: boolean;
    hasNext: boolean;
}

export interface LocalDocumentNavigationResponse extends LocalDocumentNavigationData {
    document: LocalDocumentContentResponse;
}

export interface PatientContentResponse {
    id: number;
    pesel: string;
    patientNumber: string;
    firstName: string;
    lastName: string;
}

export interface PatientsListResponse {
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    sort: LocalDocumentSortType;
    first: boolean;
    last: boolean;
    numberOfElements: number;
    empty: boolean;
    pageable: LocalDocumentsPageableResponse;
    content: PatientContentResponse[];
}

export interface LocalDocumentStatisticsResponse {
    documentNumber: number;
    patientNumber: number;
    hisExportedDocumentsNumber: number;
    archivedDocumentsNumber: number;
    categoryDocumentsMap: Record<string, number>;
}